import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowDownRight, CalendarDays, CreditCard, Download, Package, ReceiptText, TrendingUp } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const getCurrentMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    start: firstDay.toISOString().slice(0, 10),
    end: now.toISOString().slice(0, 10),
  };
};

function MetricCard({ label, value, tone = 'neutral', icon: Icon, helper }) {
  const toneClass = {
    positive: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/20',
    warning: 'text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/25',
    negative: 'text-red-300 bg-red-500/10 border-red-400/20',
    neutral: 'text-white bg-white/[0.03] border-white/10',
  }[tone];

  return (
    <div className="border border-white/10 bg-zinc-950/45 p-5">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
        <div className={`flex h-9 w-9 items-center justify-center rounded-sm border ${toneClass}`}>
          <Icon className="h-4 w-4" strokeWidth={1.7} />
        </div>
      </div>
      <p className={`text-2xl font-semibold ${tone === 'positive' ? 'text-emerald-300' : tone === 'warning' ? 'text-[#D4AF37]' : tone === 'negative' ? 'text-red-300' : 'text-white'}`}>
        {value}
      </p>
      {helper && <p className="mt-2 text-xs text-zinc-500">{helper}</p>}
    </div>
  );
}

function StatementRow({ label, value, helper, tone = 'neutral', strong = false, indent = false }) {
  const valueClass = {
    positive: 'text-emerald-300',
    negative: 'text-red-300',
    warning: 'text-[#D4AF37]',
    neutral: 'text-white',
  }[tone];

  return (
    <div className={`flex items-start justify-between gap-4 px-5 py-3 ${strong ? 'bg-white/[0.035]' : ''}`}>
      <div className={indent ? 'pl-4' : ''}>
        <p className={`${strong ? 'text-sm font-semibold text-white' : 'text-sm text-zinc-400'}`}>{label}</p>
        {helper && <p className="mt-1 text-xs text-zinc-600">{helper}</p>}
      </div>
      <p className={`shrink-0 text-right ${strong ? 'text-base font-semibold' : 'text-sm font-medium'} ${valueClass}`}>
        {value}
      </p>
    </div>
  );
}

export default function ProfitLoss() {
  const defaultRange = useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const { data } = await axios.get(`${API}/reports/profit-loss?${params.toString()}`, {
        withCredentials: true,
      });
      setReport(data);
    } catch (error) {
      console.error('Failed to fetch profit loss report:', error);
      alert(error.response?.data?.detail || 'Gagal mengambil laporan profit loss.');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const { data, headers } = await axios.get(`${API}/reports/profit-loss/export?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const contentDisposition = headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `profit-loss-${startDate || 'awal'}-${endDate || 'akhir'}.xlsx`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download profit loss excel:', error);
      alert(error.response?.data?.detail || 'Gagal download Excel profit loss.');
    } finally {
      setDownloadingExcel(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = report?.summary || {};
  const grossMargin = summary.total_revenue > 0 ? (summary.gross_profit / summary.total_revenue) * 100 : 0;
  const productMargin = summary.product_revenue > 0
    ? ((summary.product_revenue - summary.product_cogs) / summary.product_revenue) * 100
    : 0;
  const totalRevenue = Number(summary.total_revenue || 0);
  const productRevenue = Number(summary.product_revenue || 0);
  const serviceRevenue = Number(summary.service_revenue || 0);
  const operatingExpenses = Number(summary.operating_expenses || 0);
  const productGrossProfit = Number(summary.product_revenue || 0) - Number(summary.product_cogs || 0);
  const netProfit = Number(summary.net_profit || 0);
  const netMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
  const periodLabel = `${formatDate(startDate)} - ${formatDate(endDate)}`;
  const isProfit = netProfit >= 0;
  const servicePlanCategories = Array.isArray(report?.service_plan_categories) ? report.service_plan_categories : [];

  return (
    <DashboardLayout>
      <Header title="Laporan Laba Rugi" />
      <div className="p-8" data-testid="profit-loss-page">
        <div className="mb-6 overflow-hidden border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.14),transparent_30%),linear-gradient(135deg,rgba(255,255,255,0.035),rgba(9,9,11,0.82))]">
          <div className="flex flex-col gap-6 p-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <Badge className="mb-3 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                Laporan Keuangan
              </Badge>
              <h2 className="text-2xl font-semibold text-white">Laporan Laba Rugi</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-500">
                Fokus pada angka inti: pendapatan, biaya, dan laba bersih pada periode yang dipilih.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-sm border border-white/10 bg-black/20 px-2.5 py-1 text-xs text-zinc-300">
                  Periode: {periodLabel}
                </span>
                <span className={`rounded-sm border px-2.5 py-1 text-xs ${isProfit ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-red-500/30 bg-red-500/10 text-red-300'}`}>
                  Status: {isProfit ? 'Untung' : 'Rugi'}
                </span>
                <span className="rounded-sm border border-white/10 bg-black/20 px-2.5 py-1 text-xs text-zinc-300">
                  Net Margin: {formatPercent(netMargin)}
                </span>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-[160px_160px_auto_auto]">
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Dari</p>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-11 rounded-sm border-white/10 bg-black/25 text-white"
                  data-testid="profit-loss-start-date"
                />
              </div>
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Sampai</p>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-11 rounded-sm border-white/10 bg-black/25 text-white"
                  data-testid="profit-loss-end-date"
                />
              </div>
              <Button
                onClick={fetchReport}
                disabled={loading}
                className="self-end rounded-sm bg-[#D4AF37] px-6 text-black hover:bg-[#C59A27]"
                data-testid="profit-loss-filter-btn"
              >
                Terapkan
              </Button>
              <Button
                type="button"
                onClick={downloadExcel}
                disabled={loading || downloadingExcel}
                variant="outline"
                className="self-end rounded-sm border-white/10 bg-black/25 px-5 text-white hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]"
                data-testid="profit-loss-download-excel-btn"
              >
                <Download className="mr-2 h-4 w-4" strokeWidth={1.7} />
                {downloadingExcel ? 'Menyiapkan...' : 'Unduh Excel'}
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
          </div>
        ) : (
          <>
            <div className="mb-6 grid gap-4 xl:grid-cols-[1fr_1.05fr]">
              <div className={`border p-6 ${isProfit ? 'border-emerald-400/20 bg-emerald-500/10' : 'border-red-400/20 bg-red-500/10'}`}>
                <p className={`text-[11px] font-bold uppercase tracking-[0.22em] ${isProfit ? 'text-emerald-300' : 'text-red-300'}`}>
                  Hasil Akhir
                </p>
                <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Laba Bersih</h3>
                    <p className={`mt-2 text-4xl font-semibold tracking-tight ${isProfit ? 'text-emerald-200' : 'text-red-200'}`}>
                      {formatCurrency(netProfit)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="border border-white/10 bg-black/20 px-4 py-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Margin Kotor</p>
                      <p className="mt-2 text-lg font-semibold text-white">{formatPercent(grossMargin)}</p>
                    </div>
                    <div className="border border-white/10 bg-black/20 px-4 py-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Net Margin</p>
                      <p className="mt-2 text-lg font-semibold text-white">{formatPercent(netMargin)}</p>
                    </div>
                  </div>
                </div>
                <p className={`mt-5 text-sm leading-6 ${isProfit ? 'text-emerald-100/70' : 'text-red-100/70'}`}>
                  Ringkasnya: total pendapatan dikurangi HPP produk dan pengeluaran operasional.
                </p>
              </div>

              <div className="border border-white/10 bg-zinc-950/40">
                <div className="border-b border-white/10 px-5 py-4">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Rumus Sederhana</p>
                  <h3 className="mt-1 text-sm font-semibold text-white">Pendapatan - Biaya = Laba Bersih</h3>
                </div>
                <div className="divide-y divide-white/5">
                  <StatementRow
                    label="1. Total Pendapatan"
                    helper="Pendapatan layanan + penjualan produk."
                    value={formatCurrency(totalRevenue)}
                    tone="warning"
                  />
                  <StatementRow
                    label="2. HPP Produk"
                    helper="Biaya modal produk yang terjual."
                    value={`- ${formatCurrency(summary.product_cogs)}`}
                    tone="negative"
                  />
                  <StatementRow
                    label="3. Pengeluaran Operasional"
                    helper={`${summary.expense_count || 0} catatan pengeluaran tervalidasi.`}
                    value={`- ${formatCurrency(operatingExpenses)}`}
                    tone="negative"
                  />
                  <StatementRow
                    label="4. Laba Bersih"
                    value={formatCurrency(netProfit)}
                    tone={isProfit ? 'positive' : 'negative'}
                    strong
                  />
                </div>
              </div>
            </div>

            <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                label="Total Pendapatan"
                value={formatCurrency(summary.total_revenue)}
                icon={TrendingUp}
                tone="warning"
                helper={`${summary.service_transaction_count || 0} pembayaran, ${summary.product_order_count || 0} order produk`}
              />
              <MetricCard
                label="Penjualan Produk"
                value={formatCurrency(summary.product_revenue)}
                icon={Package}
                tone="neutral"
                helper={`${summary.product_item_count || 0} item terjual`}
              />
              <MetricCard
                label="HPP Produk"
                value={formatCurrency(summary.product_cogs)}
                icon={ArrowDownRight}
                tone="negative"
                helper={`Margin produk ${productMargin.toFixed(1)}%`}
              />
              <MetricCard
                label="Pengeluaran"
                value={formatCurrency(summary.operating_expenses)}
                icon={ArrowDownRight}
                tone="negative"
                helper={`${summary.expense_count || 0} catatan pengeluaran`}
              />
            </div>

            <div className="mb-6 grid gap-4 xl:grid-cols-[1.15fr_.85fr]">
              <div className="border border-white/10 bg-zinc-950/40">
                <div className="border-b border-white/10 px-5 py-4">
                  <h3 className="text-sm font-semibold text-white">Rincian Pendapatan</h3>
                  <p className="mt-1 text-xs text-zinc-500">Kategori layanan mengikuti plan terbaru: GYM, CARDIO, PT, ALL ACCESS.</p>
                </div>
                <div className="divide-y divide-white/5">
                  {servicePlanCategories.length > 0 ? (
                    servicePlanCategories.map((item) => (
                      <div key={item.category || item.label} className="flex items-center justify-between px-5 py-4">
                        <span className="text-sm text-zinc-400">{item.label || item.category || '-'}</span>
                        <span className="text-sm font-semibold text-white">{formatCurrency(item.amount)}</span>
                      </div>
                    ))
                  ) : (
                    <>
                      {[
                        ['GYM', summary.membership_revenue],
                        ['CARDIO', summary.daily_visit_revenue],
                        ['LAINNYA', summary.other_service_revenue],
                      ].map(([label, value]) => (
                        <div key={label} className="flex items-center justify-between px-5 py-4">
                          <span className="text-sm text-zinc-400">{label}</span>
                          <span className="text-sm font-semibold text-white">{formatCurrency(value)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="flex items-center justify-between bg-white/[0.02] px-5 py-4">
                    <span className="text-sm font-semibold text-white">PRODUK / POS (PAID)</span>
                    <span className="text-sm font-semibold text-white">{formatCurrency(summary.product_revenue)}</span>
                  </div>
                  <div className="flex items-center justify-between bg-white/[0.025] px-5 py-4">
                    <span className="text-sm font-semibold text-white">Laba Kotor Produk</span>
                    <span className={`text-sm font-semibold ${productGrossProfit >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                      {formatCurrency(productGrossProfit)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-5 py-4">
                    <span className="text-sm text-zinc-400">Margin Produk</span>
                    <span className={`text-sm font-semibold ${productMargin >= 0 ? 'text-[#D4AF37]' : 'text-red-300'}`}>
                      {productRevenue > 0 ? formatPercent(productMargin) : '-'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-white/10 bg-zinc-950/40">
                <div className="border-b border-white/10 px-5 py-4">
                  <h3 className="text-sm font-semibold text-white">Pengeluaran per Kategori</h3>
                  <p className="mt-1 text-xs text-zinc-500">Pengeluaran berdasarkan kategori.</p>
                </div>
                <div className="divide-y divide-white/5">
                  {(report?.expense_categories || []).length > 0 ? (
                    report.expense_categories.map((item) => (
                      <div key={item.category} className="flex items-center justify-between px-5 py-4">
                        <span className="inline-flex items-center gap-2 text-sm text-zinc-400">
                          <CreditCard className="h-4 w-4 text-zinc-600" strokeWidth={1.6} />
                          {item.category}
                        </span>
                        <span className="text-sm font-semibold text-white">{formatCurrency(item.amount)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="px-5 py-8 text-center text-sm text-zinc-600">Belum ada pengeluaran pada periode ini.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-6 border border-white/10 bg-zinc-950/40">
              <div className="border-b border-white/10 px-5 py-4">
                <h3 className="text-sm font-semibold text-white">Metode Pembayaran Layanan</h3>
                <p className="mt-1 text-xs text-zinc-500">Berdasarkan transaksi membership dan daily visit.</p>
              </div>
              <div className="grid divide-y divide-white/5 md:grid-cols-3 md:divide-x md:divide-y-0">
                {(report?.payment_methods || []).length > 0 ? (
                  report.payment_methods.map((item) => (
                    <div key={item.method} className="px-5 py-4">
                      <p className="inline-flex items-center gap-2 text-sm text-zinc-400">
                        <CreditCard className="h-4 w-4 text-zinc-600" strokeWidth={1.6} />
                        {item.method}
                      </p>
                      <p className="mt-2 text-lg font-semibold text-white">{formatCurrency(item.amount)}</p>
                    </div>
                  ))
                ) : (
                  <div className="px-5 py-8 text-center text-sm text-zinc-600 md:col-span-3">Belum ada transaksi layanan.</div>
                )}
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-3">
              <div className="border border-white/10 bg-zinc-950/40">
                <div className="border-b border-white/10 px-5 py-4">
                  <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-white">
                    <ReceiptText className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.6} />
                    Transaksi Layanan Terbaru
                  </h3>
                </div>
                <div className="divide-y divide-white/5">
                  {(report?.service_transactions || []).length > 0 ? (
                    report.service_transactions.slice(0, 8).map((item) => (
                      <div key={item.id} className="flex items-center justify-between gap-4 px-5 py-4">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-white">{item.customer}</p>
                          <p className="mt-1 truncate text-xs text-zinc-500">{item.category} - {formatDate(item.date)}</p>
                        </div>
                        <p className="shrink-0 text-sm font-semibold text-white">{formatCurrency(item.amount)}</p>
                      </div>
                    ))
                  ) : (
                    <div className="px-5 py-10 text-center text-sm text-zinc-600">Belum ada transaksi layanan.</div>
                  )}
                </div>
              </div>

              <div className="border border-white/10 bg-zinc-950/40">
                <div className="border-b border-white/10 px-5 py-4">
                  <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-white">
                    <Package className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.6} />
                    Order Produk Terbaru
                  </h3>
                </div>
                <div className="divide-y divide-white/5">
                  {(report?.product_orders || []).length > 0 ? (
                    report.product_orders.slice(0, 8).map((item) => (
                      <div key={item.id} className="grid grid-cols-[1fr_auto] gap-4 px-5 py-4">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-white">{item.customer}</p>
                          <p className="mt-1 text-xs text-zinc-500">
                            {item.total_items} item - HPP {formatCurrency(item.cogs)} - {formatDate(item.date)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-white">{formatCurrency(item.revenue)}</p>
                          <p className="mt-1 text-xs text-emerald-300">{formatCurrency(item.gross_profit)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-5 py-10 text-center text-sm text-zinc-600">Belum ada order produk paid.</div>
                  )}
                </div>
              </div>

              <div className="border border-white/10 bg-zinc-950/40">
                <div className="border-b border-white/10 px-5 py-4">
                  <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-white">
                    <ArrowDownRight className="h-4 w-4 text-red-300" strokeWidth={1.6} />
                    Pengeluaran Terbaru
                  </h3>
                </div>
                <div className="divide-y divide-white/5">
                  {(report?.expenses || []).length > 0 ? (
                    report.expenses.slice(0, 8).map((item) => (
                      <div key={item.id} className="flex items-center justify-between gap-4 px-5 py-4">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-white">{item.title}</p>
                          <p className="mt-1 truncate text-xs text-zinc-500">{item.category} - {formatDate(item.expense_date)}</p>
                        </div>
                        <p className="shrink-0 text-sm font-semibold text-red-300">{formatCurrency(item.amount)}</p>
                      </div>
                    ))
                  ) : (
                    <div className="px-5 py-10 text-center text-sm text-zinc-600">Belum ada pengeluaran.</div>
                  )}
                </div>
              </div>
            </div>

            {report?.note && (
              <div className="mt-5 flex items-start gap-2 border border-white/10 bg-white/[0.025] px-4 py-3 text-xs text-zinc-500">
                <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600" strokeWidth={1.6} />
                <span>{report.note}</span>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

